/**
 * Build the editor's {@link LayoutTarget} list from a component's **published
 * base config** ({@link PublishedLayoutConfig}), the reliable counterpart to
 * the DOM-scrape `discoverLayoutTargets`.
 *
 * Sourcing targets from config (not the rendered DOM) is the root-cause fix
 * behind several editor requirements: the config carries a component **title**,
 * **action-button** labels, each field's **placeholder** separately from its
 * label, and **hidden** fields/columns + **info items** that render no DOM
 * marker. See `.claude-work/plans/layout-editor-free-text-editing.md` §2.4.
 *
 * ## Per-bucket (multi-surface aware)
 *
 * Targets are emitted from **each present config bucket independently** — not
 * from an exclusive switch on `hostKind`. This lets a single component publish
 * BOTH a form surface (`fields`/`sections`/`actions`) AND a table surface
 * (`columns`) in one config and expose every surface's targets at once (e.g. a
 * card that embeds an EntityForm above a DataList):
 *
 * - `fields` present → one `'field'` target per `fields[name]` (INCLUDING
 *   `visible:false` ones, so hidden fields stay listed).
 * - `sections` present → one `'section'` target per `sections[]`.
 * - `actions` present → one `'action'` target per defined action
 *   (submit/cancel/reset/custom).
 * - `columns` present → one `'column'` target per `columns[key]` (INCLUDING
 *   `hidden:true`).
 * - `filters` present → one `'filter'` target per `filters[]` (INCLUDING
 *   `hidden:true`; rename / hide / show / reorder + placeholder, no
 *   tooltip/helper).
 * - `search` present → ONE `'search'` target (placeholder + hide/show only; no
 *   rename-label, no tooltip/helper, no reorder).
 * - `items` present → one `'field'`-kind target per item (reusing the
 *   rename/visible/tooltip/order affordances; items have no placeholder/helper).
 *
 * The single `'title'` target (key `'__title__'`) is gated by `hostKind`, NOT
 * by which buckets are present: it is emitted **always** for `'entity-form'`
 * and `'data-list'`, and **only when a title is published** for `'info-list'`
 * (InfoList has no config title of its own). This preserves the original
 * per-host title rule exactly.
 *
 * Emission order: title (for the title-always hosts) is appended last; within
 * the body the buckets emit fields → sections → actions → columns → filters →
 * search → items.
 * For a config that sets exactly ONE surface bucket (the only kind that exists
 * today) this is byte-identical to the previous per-host implementation:
 * entity-form was fields → sections → actions → title; data-list was columns →
 * title; info-list was items → (title only when set). Declaration / `order`
 * sequence is preserved within each bucket so the panel shows targets stably.
 *
 * Returns `[]` for an `undefined` published config (the editor then falls back
 * to the DOM path).
 *
 * Pure, React-free — unit-testable in isolation.
 */

import type {
  FieldType,
  SelectFieldOptions,
} from '@components/integrated/entity-form';
import type { PublishedLayoutConfig } from '@components/shared/configurable';

import type { LayoutTarget } from './layoutAuthoring';

/** A published field config entry (an EntityForm `FieldConfig`). */
type PublishedField = NonNullable<PublishedLayoutConfig['fields']>[string];

/** A published toolbar-filter config entry (a DataList `FilterConfig`). */
type PublishedFilter = NonNullable<PublishedLayoutConfig['filters']>[number];

/** The published toolbar-search config (a DataList search descriptor). */
type PublishedSearch = NonNullable<PublishedLayoutConfig['search']>;

/**
 * Resolve a field's base placeholder for the editor, surfacing it regardless of
 * where the field type stores it. Text-like inputs keep the placeholder at the
 * top level (`FieldConfig.placeholder`); SELECT / MULTISELECT fields keep it
 * under `options.placeholder` ({@link SelectFieldOptions}). Reading only the
 * top-level slot left selects with a blank Placeholder input even though the
 * rendered select shows it. The top-level value wins when both are present;
 * otherwise the select-options placeholder is used. `undefined` when neither
 * carries one (e.g. checkbox/date/file, whose option shapes have no
 * placeholder).
 */
function resolveBasePlaceholder(field: PublishedField): string | undefined {
  if (field.placeholder !== undefined) return field.placeholder;
  if (field.type === 'select' || field.type === 'multiselect') {
    return (field.options as SelectFieldOptions | undefined)?.placeholder;
  }
  return undefined;
}

/** Stable key the single title target carries. */
export const TITLE_TARGET_KEY = '__title__';

/** Stable key the single toolbar-search target carries. */
export const SEARCH_TARGET_KEY = '__search__';

/**
 * Field types that render a placeholder. Text-like inputs (and the select
 * trigger) take a placeholder; checkbox / checkboxGroup / radio / date /
 * dateRange / file do not. `multiselect` is a select variant and also takes a
 * placeholder.
 */
const PLACEHOLDER_FIELD_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'text',
  'number',
  'email',
  'password',
  'tel',
  'url',
  'textarea',
  'select',
  'multiselect',
]);

/**
 * Whether a field of the given type supports a placeholder input in the editor.
 */
function fieldSupportsPlaceholder(type: FieldType): boolean {
  return PLACEHOLDER_FIELD_TYPES.has(type);
}

/**
 * Build a single `'title'` target. `baseLabel` is the resolved base title (or
 * `''` so the admin can fill an empty title in). The title only supports a
 * label/rename input — no placeholder/helper/tooltip.
 */
function titleTarget(title: string | undefined): LayoutTarget {
  return {
    kind: 'title',
    key: TITLE_TARGET_KEY,
    baseLabel: title ?? '',
  };
}

/**
 * Discover the editor targets a published config exposes. Emits the targets of
 * **each present bucket independently** (so a multi-surface component exposes
 * its form AND table targets at once), then appends the title target per the
 * `hostKind` title rule. See the module doc-comment for the full contract and
 * the single-surface equivalence guarantee.
 */
export function discoverConfigTargets(
  published: PublishedLayoutConfig | undefined,
): LayoutTarget[] {
  if (published === undefined) return [];

  const targets: LayoutTarget[] = [];

  // Each surface bucket emits independently, in a stable group order:
  // fields → sections → actions → columns → items. For a single-surface
  // config only one of these contributes, reproducing the previous per-host
  // body order exactly.
  if (published.fields !== undefined) pushFieldTargets(targets, published.fields);
  if (published.sections !== undefined)
    pushSectionTargets(targets, published.sections);
  if (published.actions !== undefined)
    pushActionTargets(targets, published.actions);
  if (published.columns !== undefined)
    pushColumnTargets(targets, published.columns);
  if (published.filters !== undefined)
    pushFilterTargets(targets, published.filters);
  if (published.search !== undefined)
    pushSearchTarget(targets, published.search);
  if (published.items !== undefined) pushItemTargets(targets, published.items);

  // Title — gated by `hostKind`, NOT by which buckets are present, to preserve
  // the original per-host rule byte-for-byte: always for entity-form/data-list,
  // only-when-published for info-list. Appended last.
  if (published.hostKind === 'info-list') {
    if (published.title !== undefined) {
      targets.push(titleTarget(published.title));
    }
  } else {
    targets.push(titleTarget(published.title));
  }

  return targets;
}

/**
 * Field targets — one per entry, INCLUDING `visible:false` (req #6: hidden
 * fields stay listed). Declaration order is the `Object.entries` insertion
 * order. Every non-section field can carry a helper text and a tooltip.
 */
function pushFieldTargets(
  targets: LayoutTarget[],
  fields: NonNullable<PublishedLayoutConfig['fields']>,
): void {
  for (const [key, field] of Object.entries(fields)) {
    // Selects store the placeholder under `options.placeholder`, not at the top
    // level — resolve from both so a select's placeholder is not blank.
    const basePlaceholder = resolveBasePlaceholder(field);
    // A field is baseline-required when its validation declares `required`
    // (either `true` for the default message or a string for a custom one),
    // mirroring how `baseHelperText` is read from the published config.
    const required = field.validation?.required;
    const baseRequired = required === true || typeof required === 'string';
    targets.push({
      kind: 'field',
      key,
      baseLabel: field.label,
      ...(basePlaceholder !== undefined
        ? { basePlaceholder }
        : {}),
      ...(field.helperText !== undefined
        ? { baseHelperText: field.helperText }
        : {}),
      ...(field.tooltip !== undefined ? { baseTooltip: field.tooltip } : {}),
      ...(field.visible === false ? { baseHidden: true } : {}),
      ...(baseRequired ? { baseRequired: true } : {}),
      supportsPlaceholder: fieldSupportsPlaceholder(field.type),
      supportsHelperText: true,
      supportsTooltip: true,
      // Add-only: the front-end-required toggle is offered ONLY for fields that
      // are NOT already baseline-required (we never relax a required field).
      supportsRequired: !baseRequired,
      // Cocher / Décocher write a boolean, so only a single `checkbox` field
      // (never a `checkboxGroup`, whose value is a string array) takes them.
      supportsCheckedCondition: field.type === 'checkbox',
    });
  }
}

/**
 * Section targets — title is the editable label; sections support neither
 * placeholder/helper nor a tooltip. Declaration order preserved.
 */
function pushSectionTargets(
  targets: LayoutTarget[],
  sections: NonNullable<PublishedLayoutConfig['sections']>,
): void {
  for (const section of sections) {
    targets.push({
      kind: 'section',
      key: section.id,
      baseLabel: section.title ?? section.id,
    });
  }
}

/**
 * Action targets — one per defined named action (submit/cancel/reset) plus each
 * custom action. Label is editable; the panel also offers a visibility toggle.
 * No placeholder/helper/tooltip.
 */
function pushActionTargets(
  targets: LayoutTarget[],
  actions: NonNullable<PublishedLayoutConfig['actions']>,
): void {
  for (const key of ['submit', 'cancel', 'reset'] as const) {
    const action = actions[key];
    if (action !== undefined) {
      targets.push({
        kind: 'action',
        key,
        baseLabel: action.label ?? key,
        ...(action.visible === false ? { baseHidden: true } : {}),
      });
    }
  }
  for (const custom of actions.custom ?? []) {
    targets.push({
      kind: 'action',
      key: custom.id,
      baseLabel: custom.label ?? custom.id,
      ...(custom.visible === false ? { baseHidden: true } : {}),
    });
  }
}

/**
 * Column targets — one per entry, INCLUDING `hidden:true`. Columns carry a
 * label and a header tooltip but no placeholder/helper. Declaration order
 * preserved.
 */
function pushColumnTargets(
  targets: LayoutTarget[],
  columns: NonNullable<PublishedLayoutConfig['columns']>,
): void {
  for (const [key, column] of Object.entries(columns)) {
    targets.push({
      kind: 'column',
      key,
      baseLabel: column.label,
      ...(column.headerTooltip !== undefined
        ? { baseTooltip: column.headerTooltip }
        : {}),
      ...(column.hidden === true ? { baseHidden: true } : {}),
      supportsTooltip: true,
    });
  }
}

/**
 * Toolbar-filter targets — one `'filter'` target per entry, INCLUDING
 * `hidden:true` (baseline-hidden filters render no DOM marker, so they would
 * vanish without the config path). Filters carry a label, a placeholder, and
 * `hidden`; they have **no** tooltip/helper. Declaration order preserved.
 */
function pushFilterTargets(
  targets: LayoutTarget[],
  filters: readonly PublishedFilter[],
): void {
  for (const filter of filters) {
    targets.push({
      kind: 'filter',
      key: filter.id,
      baseLabel: filter.label,
      ...(filter.placeholder !== undefined
        ? { basePlaceholder: filter.placeholder }
        : {}),
      ...(filter.hidden === true ? { baseHidden: true } : {}),
      supportsPlaceholder: true,
    });
  }
}

/**
 * Toolbar-search target — exactly ONE `'search'` target (the single search
 * box), keyed on {@link SEARCH_TARGET_KEY}. It supports a placeholder edit and
 * a hide/show toggle only: no rename-label (search has no display label), no
 * tooltip/helper, no reorder. `baseHidden` is `true` when `search.enabled` is
 * explicitly `false`.
 */
function pushSearchTarget(
  targets: LayoutTarget[],
  search: PublishedSearch,
): void {
  targets.push({
    kind: 'search',
    key: SEARCH_TARGET_KEY,
    // Search has no rename-label; keep `baseLabel` empty so any accidental
    // label reader degrades gracefully.
    baseLabel: '',
    ...(search.placeholder !== undefined
      ? { basePlaceholder: search.placeholder }
      : {}),
    ...(search.enabled === false ? { baseHidden: true } : {}),
    supportsPlaceholder: true,
  });
}

/**
 * Info-item targets — one `'field'`-kind target per item (rename / hide /
 * reorder / tooltip; no placeholder/helper). Declaration order preserved.
 */
function pushItemTargets(
  targets: LayoutTarget[],
  items: NonNullable<PublishedLayoutConfig['items']>,
): void {
  for (const item of items) {
    targets.push({
      kind: 'field',
      key: item.key,
      baseLabel: item.label ?? item.key,
      ...(item.tooltip !== undefined ? { baseTooltip: item.tooltip } : {}),
      ...(item.visible === false ? { baseHidden: true } : {}),
      // Info items have no placeholder/helper; they do carry a tooltip.
      supportsTooltip: true,
    });
  }
}
